import {Component, forwardRef, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {IconType, IoniconsOutline, PasswordInputState, REQUIRED_VALIDATOR_KEY} from '@pt/pt-models';
import {PasswordParamsResponse} from '@primatoo-ms-organization';
import {AsyncValidator, ValidatorGenerator} from '../../utils';
import {CheckPasswordService} from "@pt/api-client";

@Component({
  selector: 'pt-input-confirm-password',
  templateUrl: './input-confirm-password.component.html',
  styleUrls: ['./input-confirm-password.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputConfirmPasswordComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputConfirmPasswordComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class InputConfirmPasswordComponent implements OnInit, OnDestroy, ControlValueAccessor {

  // il controlName viene utilizzato SOLO per la generazione di un ID univoco
  @Input() help: boolean;
  @Input() controlName: string;
  @Input() passwordHelpParams: PasswordParamsResponse;
  @Input() passwordLabel: string;
  @Input() confirmPasswordLabel: string;
  @Input() requiredPassword = true;
  @Input() requiredConfirmPassword = true;
  @Input() checkPasswordService: CheckPasswordService;

  passwordInputState = new PasswordInputState();

  REQUIRED = ValidatorGenerator.generateWidgetValidator(REQUIRED_VALIDATOR_KEY);

  public formGroup: FormGroup;
  iconKey = IoniconsOutline.infoCircle;
  iconType = IconType.IONIC_ICON;
  changedConfirmPassword = false;
  changedPassword = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  get password() {
    return this.formGroup?.controls['password'];
  }

  get confirmPassword() {
    return this.formGroup?.controls['confirmPassword'];
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.setFormGroup();

    this.password.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((t) => {
      this.changedPassword = t !== null;
      this.formGroup.controls.confirmPassword.updateValueAndValidity();
      this.emitChange(t);
    });

    this.confirmPassword.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((t) => {
      this.changedConfirmPassword = t !== null;
    });

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((t) => {
      if (this.onChanged) {
        this.onChanged(this.formGroup?.valid ? t.password : null);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });
  }

  writeValue(obj: any): void {
    this.formGroup.controls.password.setValue(obj);
    this.formGroup.controls.confirmPassword.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.controls.password.disable();
      this.formGroup.controls.confirmPassword.disable();
    } else {
      this.formGroup.controls.password.enable();
      this.formGroup.controls.confirmPassword.enable();
    }
  }

  validate(value: FormControl) {
    return this.formGroup.invalid && {
      invalid: true
    };
  }

  private matchValues(
    matchTo: string // name of the control to match to
  ): (AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      return !!control.parent && !!control.parent.value &&
      control.value === control.parent.controls[matchTo].value ? null : {noMatching: true};
    };
  }

  private setFormGroup() {
    const asyncValidator = this.checkPasswordService ? AsyncValidator.checkPassword(this.checkPasswordService) : null;
    this.formGroup = new FormGroup({
      password: new FormControl(null, [Validators.required], asyncValidator),
      confirmPassword: new FormControl(null, [
        Validators.required, this.matchValues('password')
      ])
    });
  }

  private emitChange(value) {
    const pending = this.formGroup?.controls['password'].pending;
    if (pending) {
      setTimeout(() => this.emitChange(value), 100);
    } else {
      if (this.formGroup?.valid) {
        this.emitChangeValue(value);
      } else {
        this.emitChangeValue(null);
      }
    }
  }

  private emitChangeValue(value: string | null) {
    if (this.onChanged) {
      this.onChanged(value);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }
}
